import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { useMemo, useState, useRef } from "react";
import { BarAndLineChart } from "../../../../components/charts/BarAndLineChart/BarAndLineChart";
import {
  BarAndLineChartDataProps,
  BarSize,
} from "../../../../components/charts/BarAndLineChart/types";
import { BaseChartColors } from "../../../../components/charts/constants";
import { ChartWrapper } from "../../../../components/ChartWrapper";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import { toCustomSpendCategory } from "../../../../utils/workspace/customSpendCategory";
import { sortDataBySpend, mapSpendBreakdownResultToChartData } from "./utils";
import { useGetSpendByCountryData } from "./hooks/useGetSpendByCountryData";
import { useGetSpendByRegionData } from "./hooks/useGetSpendByRegionData";
import { determineChartContent, hasNoData } from "../../utils";
import { useCombinedFilters } from "../../../../hooks/useCombinedFilters";
import * as am4charts from "@amcharts/amcharts4/charts";
import { CHART_CONSTANTS } from "../constants";

const { baseId, chartTitle, tooltip } = CHART_CONSTANTS.marketSizeByGeography;

const generateGeographySegmentForCSV = (segment: string) => {
  return segment === "countries" ? "Country" : "Region";
};

interface MarketSizeByGeographyChartProps {
  barSize?: BarSize;
  applyWorkspaceFiltersOnly?: boolean;
}

export function MarketSizeByGeographyChart({
  barSize,
  applyWorkspaceFiltersOnly = false,
}: MarketSizeByGeographyChartProps = {}) {
  const [selectedSegment, setSelectedSegment] = useState("countries");
  const chartRef = useRef<am4charts.XYChart>(null);
  const spendCategoriesWeights = useWorkspaceStore(
    (state) => state.draft?.configuration.tam.spendCategoryWeights
  );
  const spendType = useWorkspaceStore(
    (state) => state.draft?.configuration.tam.spendType
  );
  const filters = useCombinedFilters(applyWorkspaceFiltersOnly);
  const localizeInstalls = useWorkspaceStore(
    (state) => state.draft?.configuration?.localizeInstalls ?? true
  );
  const { loading: countryLoading, spendByCountryChartData } =
    useGetSpendByCountryData({
      opportunityGeneratorFilters: filters || {},
      customSpendCategory: toCustomSpendCategory(
        spendCategoriesWeights,
        spendType
      ),
      localizeInstalls,
    });

  const { loading: regionLoading, spendByRegionChartData } =
    useGetSpendByRegionData({
      companyFilters: filters || {},
      customSpendCategory: toCustomSpendCategory(
        spendCategoriesWeights,
        spendType
      ),
      localizeInstalls,
    });

  const top5Countries = useMemo(
    () => sortDataBySpend(spendByCountryChartData).slice(0, 5),
    [spendByCountryChartData]
  );

  const dataMap = useMemo<{ [key: string]: BarAndLineChartDataProps[] }>(() => {
    return {
      countries: mapSpendBreakdownResultToChartData(top5Countries),
      regions: mapSpendBreakdownResultToChartData(spendByRegionChartData),
    };
  }, [top5Countries, spendByRegionChartData]);

  const isLoading = countryLoading || regionLoading;

  const noDataReturned = useMemo(
    () => hasNoData(isLoading, dataMap[selectedSegment]),
    [dataMap, selectedSegment, isLoading]
  );

  const csvFieldMappings = useMemo(
    () => ({
      category: generateGeographySegmentForCSV(selectedSegment),
      barSeriesValue: "Spend (USD)",
      lineSeriesValue: "No. Of Companies",
    }),
    [selectedSegment]
  );

  function handleSegmentSelect(event: SelectChangeEvent) {
    setSelectedSegment(event.target.value);
  }

  const chartContent = useMemo(() => {
    return determineChartContent({
      isLoading,
      noDataReturned,
      baseId,
      chartToRender: (
        <BarAndLineChart
          id={`${baseId}-chart`}
          chartData={dataMap[selectedSegment] || []}
          xAxisLabel="Spend (USD)"
          barSeriesColor={BaseChartColors.Blue}
          barSeriesName="SPEND (USD)"
          lineSeriesName="NO. OF COMPANIES"
          barSize={barSize}
          ref={chartRef}
        />
      ),
    });
  }, [isLoading, selectedSegment, dataMap, noDataReturned, barSize]);

  return (
    <ChartWrapper
      id={`${baseId}-chart-wrapper`}
      title={chartTitle}
      tooltipTitle={tooltip}
      chart={chartContent}
      chartRef={chartRef}
      noData={noDataReturned}
      isLoading={isLoading}
      csvFieldMappings={csvFieldMappings}
      titleExtras={
        <FormControl sx={{ minWidth: 110, maxWidth: "100%" }}>
          <Select
            size="small"
            value={selectedSegment}
            onChange={handleSegmentSelect}
            id={`${baseId}-segment-select`}
          >
            <MenuItem value="countries">Segment: Countries</MenuItem>
            <MenuItem value="regions">Segment: Regions</MenuItem>
          </Select>
        </FormControl>
      }
    />
  );
}
